import type { AlwaysAiUser } from "../../../shared/api-types.js";
import type {
  NotificationListItem,
  NotificationListPage,
  NotificationReadResult,
  NotificationUnreadCount,
} from "../../../shared/notification-list.js";
import { formatAppointmentDate, formatAppointmentTime } from "./appointments.js";
import { hasPracticeSection } from "./rail-sections.js";

// The RD's in-app notification list (NO-830): request helpers over an injected
// transport, the rail count's label and polling gate, and the read mark's
// optimistic update. Pure and relative-import only, so `tsx --test` runs it.

export type NotificationsRequest = <T>(path: string, init?: RequestInit) => Promise<T>;

const BASE = "/api/notifications";

export const NOTIFICATION_QUERY_KEYS = {
  list: ["notifications", "list"] as const,
  unreadCount: ["notifications", "unread-count"] as const,
};

/** How often the rail asks for the unread count while its row is on screen. */
export const UNREAD_COUNT_POLL_MS = 60_000;

export function createNotificationsClient(request: NotificationsRequest) {
  return {
    /** One page, newest first; `cursor` is the previous page's opaque `nextCursor`. */
    async list(cursor: string | null = null): Promise<NotificationListPage> {
      const path = cursor ? `${BASE}?cursor=${encodeURIComponent(cursor)}` : BASE;

      return (await request<{ data: NotificationListPage }>(path)).data;
    },
    async unreadCount(): Promise<number> {
      return (await request<{ data: NotificationUnreadCount }>(`${BASE}/unread-count`))
        .data.unread;
    },
    async markRead(id: string): Promise<NotificationReadResult> {
      const path = `${BASE}/${encodeURIComponent(id)}/read`;

      return (await request<{ data: NotificationReadResult }>(path, { method: "POST" }))
        .data;
    },
  };
}

export type NotificationsClient = ReturnType<typeof createNotificationsClient>;

/** What the rail row shows: nothing at zero, 99+ past ninety-nine. */
export function badgeLabel(unread: number): string | null {
  if (!Number.isFinite(unread) || unread <= 0) return null;

  return unread > 99 ? "99+" : String(Math.floor(unread));
}

/**
 * The count is asked for only when the rail shows the row: the flag on and
 * the My practice gate holding.
 */
export function unreadCountPollEnabled(
  user: AlwaysAiUser,
  flags: { rdHomeEnabled: boolean; notificationsEnabled: boolean }
): boolean {
  return flags.notificationsEnabled && hasPracticeSection(user, flags.rdHomeEnabled);
}

/** The page is the rail row's destination, so it has the same gate minus rd-home. */
export function notificationsPageState(input: {
  frame: "rail" | "classic" | null;
  featuresLoading: boolean;
  notificationsEnabled: boolean;
  canReadOwnAppointments: boolean;
}): "blank" | "not_found" | "list" {
  if (input.frame !== "rail") return "not_found";

  if (input.featuresLoading) return "blank";

  return input.notificationsEnabled && input.canReadOwnAppointments
    ? "list"
    : "not_found";
}

/** The list as the infinite query holds it: every page loaded so far, in order. */
export interface NotificationPages {
  pages: NotificationListPage[];
  pageParams: unknown[];
}

/** The page after `page`, or null when it was the last (the infinite query's next param). */
export function nextNotificationCursor(page: NotificationListPage): string | null {
  return page.nextCursor;
}

/** Every loaded item, newest first. */
export function notificationItems(
  data: NotificationPages | undefined
): NotificationListItem[] {
  return data ? data.pages.flatMap((page) => page.items) : [];
}

/** Whether an older page exists past the last one loaded. */
export function hasOlderNotifications(data: NotificationPages | undefined): boolean {
  return Boolean(data?.pages.at(-1)?.nextCursor);
}

function pageWithItemRead(
  page: NotificationListPage,
  id: string,
  at: string
): NotificationListPage {
  return {
    ...page,
    items: page.items.map((item) =>
      item.id === id && item.readAt === null ? { ...item, readAt: at } : item
    ),
  };
}

/** The loaded pages with one item marked read now, on whichever page holds it. */
export function withItemRead(
  data: NotificationPages | undefined,
  id: string,
  at: string
): NotificationPages | undefined {
  if (!data) return data;

  return { ...data, pages: data.pages.map((page) => pageWithItemRead(page, id, at)) };
}

/** The unread count after the item opens: one less, never below zero. */
export function countAfterRead(count: number | undefined, item: NotificationListItem) {
  if (count === undefined || item.readAt !== null) return count;

  return Math.max(0, count - 1);
}

const APPOINTMENT_PATH =
  /^\/appointments\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/** The Appointment id an item links to, or null when it links nowhere. */
export function appointmentIdOf(item: Pick<NotificationListItem, "appointmentPath">) {
  const match = item.appointmentPath
    ? APPOINTMENT_PATH.exec(item.appointmentPath)
    : null;

  return match ? match[1] : null;
}

/**
 * "Friday, October 30, 2026 · 11:00 AM" in the viewer's zone. An item that
 * links nowhere is neutral, so its time is withheld (null), not "not set".
 */
export function notificationTimeLabel(
  item: Pick<NotificationListItem, "appointmentPath" | "appointmentStartsAt">,
  timezone: string
): string | null {
  if (item.appointmentPath === null) return null;

  const startsAt = item.appointmentStartsAt;
  if (!startsAt) return "Time not set";

  return `${formatAppointmentDate(startsAt, timezone)} · ${formatAppointmentTime(startsAt, timezone)}`;
}
